<template>
  <div class="relative z-50" ref="dateFilterRef">
    <!-- Trigger Button -->
    <button
      @click="toggleDropdown"
      ref="buttonRef"
      class="cursor-pointer px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit outline-none focus:outline-none bg-white transition-colors"
      :class="className"
    >
      <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 16 16" fill="none">
        <path d="M12.6667 2.66667H3.33333C2.59695 2.66667 2 3.26362 2 4V13.3333C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3333V4C14 3.26362 13.403 2.66667 12.6667 2.66667Z" stroke="#2563EB" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M10.6667 1.33334V4.00001" stroke="#2563EB" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M5.33333 1.33334V4.00001" stroke="#2563EB" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M2 6.66667H14" stroke="#2563EB" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="text-base font-bold text-blue-600">{{ displayText }}</span>
    </button>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded shadow-lg z-50"
    >
      <div class="p-2">
        <!-- Preset Options -->
        <div class="flex flex-col">
          <button
            v-for="preset in presets"
            :key="preset.value"
            @click="selectPreset(preset.value)"
            class="w-full text-left px-3 py-2 text-sm font-semibold text-gray-600 hover:bg-blue-50 rounded outline-none focus:outline-none"
            :class="{ 'bg-blue-100 text-blue-600': selectedPreset === preset.value }"
          >
            {{ preset.label }}
          </button>
        </div>

        <!-- Custom Date Range -->
        <div v-if="selectedPreset === 'custom'" class="mt-2 px-2">
          <div class="flex items-end">
            <!-- From Date -->
            <div class="flex-1 mr-2">
              <label for="date-from" class="block text-xs font-semibold text-gray-600 mb-1">From</label>
              <input
                id="date-from"
                type="date"
                v-model="customFromDate"
                :max="customToDate"
                class="w-full h-9 px-2 py-1 text-xs text-gray-800 border border-gray-300 rounded focus:border-blue-400 focus:outline-none"
              />
            </div>

            <span class="pb-2 text-gray-500 font-semibold">-</span>

            <!-- To Date -->
            <div class="flex-1 ml-2">
              <label for="date-to" class="block text-xs font-semibold text-gray-600 mb-1">To</label>
              <input
                id="date-to"
                type="date"
                v-model="customToDate"
                :min="customFromDate"
                class="w-full h-9 px-2 py-1 text-xs text-gray-800 border border-gray-300 rounded focus:border-blue-400 focus:outline-none"
              />
            </div>
          </div>

          <!-- Apply Button -->
          <div class="mt-3 text-right">
            <button
              @click="applyCustomRange"
              class="px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded hover:bg-blue-600 transition-colors outline-none focus:outline-none "
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DateFilter",
  props: {
    value: {
      type: String,
      default: "this_month"
    },
    className: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isOpen: false,
      selectedPreset: this.value,
      customFromDate: "",
      customToDate: "",
      presets: [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "This Week", value: "this_week" },
        { label: "This Month", value: "this_month" },
        { label: "Custom", value: "custom" }
      ]
    };
  },
  computed: {
    displayText() {
      if (this.selectedPreset === "custom" && this.customFromDate && this.customToDate) {
        return this.formatDate(this.customFromDate) + " - " + this.formatDate(this.customToDate);
      }
      const preset = this.presets.find(p => p.value === this.selectedPreset);
      return preset ? preset.label : "This Month";
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectPreset(value) {
      this.selectedPreset = value;
      if (value !== "custom") {
        this.isOpen = false;
        const dates = this.getPresetDates(value);
        this.$emit("input", value);
        this.$emit("change", { preset: value, ...dates });
      }
    },
    applyCustomRange() {
      if (this.customFromDate && this.customToDate) {
        this.isOpen = false;
        this.selectedPreset = "custom";
        this.$emit("input", "custom");
        this.$emit("change", {
          preset: "custom",
          from: this.customFromDate,
          to: this.customToDate
        });
      }
    },
    getPresetDates(preset) {
      const today = new Date();
      const format = date => date.toISOString().split("T")[0];
      switch (preset) {
        case "today":
          return { from: format(today), to: format(today) };
        case "yesterday": {
          const y = new Date(today);
          y.setDate(y.getDate() - 1);
          return { from: format(y), to: format(y) };
        }
        case "this_week": {
          const firstDay = new Date(today);
          const day = firstDay.getDay();
          const diff = firstDay.getDate() - day + (day === 0 ? -6 : 1);
          firstDay.setDate(diff);
          return { from: format(firstDay), to: format(today) };
        }
        case "this_month": {
          const start = new Date(today.getFullYear(), today.getMonth(), 1);
          return { from: format(start), to: format(today) };
        }
        default:
          return { from: "", to: "" };
      }
    },
    formatDate(dateString) {
      const d = new Date(dateString);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
      });
    },
    handleClickOutside(e) {
      if (this.$refs.dateFilterRef && !this.$refs.dateFilterRef.contains(e.target)) {
        this.isOpen = false;
      }
    }
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    // Emit default preset range on mount so parent receives initial dates
    const initial = this.selectedPreset || this.value || 'this_month';
    if (initial !== 'custom') {
      const dates = this.getPresetDates(initial);
      this.$emit('input', initial);
      this.$emit('change', { preset: initial, ...dates });
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  watch: {
    // Keep internal state in sync if parent updates the value
    value(newVal) {
      if (newVal && newVal !== this.selectedPreset) {
        this.selectedPreset = newVal;
        if (newVal !== 'custom') {
          const dates = this.getPresetDates(newVal);
          this.$emit('change', { preset: newVal, ...dates });
        }
      }
    }
  }
};
</script>
